/**
 * Argo agent-worker client — the single seam to the real backend.
 *
 * Every "is this agent actually doing something, or just simulating?"
 * question routes through here. When `VITE_AGENT_API_URL` is set, this
 * dispatches to the hosted Argo agent-worker (a provider-agnostic LLM
 * pipeline — Anthropic, OpenAI, or Google Gemini, chosen per the operator's
 * Model setting) and returns its result. When it's NOT set — local dev,
 * demos, preview — `isConfigured()` is false and callers fall back to the
 * deterministic in-app simulation. Nothing else in the app talks to the
 * network for agent work, so swapping simulation for the live pipeline is
 * a one-flag change with no UI rewiring.
 *
 * The wire contract is intentionally small and JSON-safe so the worker can
 * be implemented in any runtime:
 *
 *   POST {base}/interpret  { message, context }      → { text, actions }
 *   POST {base}/run        { agent, snapshot }        → { text }
 *
 * `context`/`snapshot` carry the live store data the worker needs to
 * resolve names to ids; `actions` come back already resolved so the
 * confirm-each-write cards can show exactly what will be written.
 */

import type { AgentContext, AgentReply } from './types';
import { buildAgentContext } from './context';
import { sanitizeWorkerAction } from './proposalWhy';
import { auth } from '../firebase';
import type { BrandBrain, BrandScope } from '../brandBrain/schema';
import type { BrandProvenanceMeta } from '../brandBrain/inject';
import { brandInjectionHook, type BrandInjection } from '../brandBrain/workerBridge';
import { getSelectedModel } from '../llm/modelPreference';

const BASE_URL: string | undefined = (
  import.meta.env.VITE_AGENT_API_URL as string | undefined
)?.replace(/\/+$/, '');
const API_KEY = import.meta.env.VITE_AGENT_API_KEY as string | undefined;

/** Default per-call timeout. The worker should answer well inside this. */
const TIMEOUT_MS = 30_000;

/**
 * The bearer the worker verifies. Preferred: the signed-in user's Firebase ID
 * token, which the worker validates against Google's public keys so the
 * endpoint isn't an open proxy on the operator's Anthropic key. Falls back to
 * a static VITE_AGENT_API_KEY when Firebase auth isn't active (dev/preview).
 */
async function authBearer(): Promise<string | undefined> {
  try {
    const token = await auth?.currentUser?.getIdToken();
    if (token) return token;
  } catch {
    // Fall through to the static key.
  }
  return API_KEY;
}

// ---------------------------------------------------------------------------
// Brand governance (additive) — the Brand Brain fragment rides every call
// ---------------------------------------------------------------------------

/**
 * Optional brand governance for a worker call. When provided, the composed
 * Brand Brain fragment for `scope` is prepended to the worker's system
 * prompt (sent as the additive `brandFragment` wire field the worker
 * prefixes to its prompt) and the reply carries `brandProvenance` +
 * `brandBadge` so downstream envelopes can prove exactly which brand
 * knowledge shaped the output.
 */
export interface BrandPromptOptions {
  brain: BrandBrain;
  scope: BrandScope;
  /** Prompt-token budget for the fragment. Default: 600. */
  tokenBudget?: number;
}

/** Default fragment budget — small enough to never crowd the task prompt. */
const DEFAULT_BRAND_TOKEN_BUDGET = 600;

/**
 * Resolve the brand injection for a call. Total by construction: the hook
 * never throws (compose errors degrade to the neutral fallback inside
 * brandInjectionHook), so brand governance can never block generation —
 * the shipped deterministic-fallback path stays intact.
 */
function resolveBrandInjection(brand?: BrandPromptOptions): BrandInjection | null {
  if (!brand) return null;
  return brandInjectionHook({
    scope: brand.scope,
    tokenBudget: brand.tokenBudget ?? DEFAULT_BRAND_TOKEN_BUDGET,
    brain: brand.brain,
  });
}

/** Reply fields stamped when a call was brand-governed. */
export interface BrandStampedFields {
  brandProvenance?: BrandProvenanceMeta;
  brandBadge?: string;
}

/** True when a real agent-worker endpoint is configured. */
export function isConfigured(): boolean {
  return Boolean(BASE_URL);
}

/** Human-readable backend descriptor for status lines / debugging. */
export function workerInfo(): { configured: boolean; baseUrl: string | null } {
  return { configured: isConfigured(), baseUrl: BASE_URL ?? null };
}

/** Thrown when the worker is unreachable or returns a non-OK / malformed reply. */
export class AgentWorkerError extends Error {
  constructor(message: string, readonly cause?: unknown) {
    super(message);
    this.name = 'AgentWorkerError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  if (!BASE_URL) {
    throw new AgentWorkerError('Argo agent-worker is not configured.');
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const bearer = await authBearer();
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!res.ok) {
      throw new AgentWorkerError(`Agent-worker responded ${res.status} ${res.statusText}`);
    }
    return (await res.json()) as T;
  } catch (err) {
    if (err instanceof AgentWorkerError) throw err;
    throw new AgentWorkerError('Failed to reach the Argo agent-worker.', err);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Ask the worker to interpret a chat message into proposed actions. The
 * response is shape-checked before it reaches the confirm-each-write UI so
 * a malformed payload surfaces as an error rather than a broken card.
 */
export async function interpret(
  message: string,
  context: AgentContext,
  brand?: BrandPromptOptions,
): Promise<AgentReply & BrandStampedFields> {
  const injection = resolveBrandInjection(brand);
  // The operator's chosen model (from Admin → Settings). The worker validates
  // it against its allow-list and falls back to the instance default.
  const body: Record<string, unknown> = { message, context, model: getSelectedModel() };
  // Additive wire field: a compact, size-guarded distillation of OUR data —
  // top Brain facts (provenance-tagged) + CRM stat rollups — so the worker's
  // answers and proposals are grounded in the live store, not vibes.
  body.grounding = buildAgentContext({
    people: context.people,
    accounts: context.accounts,
    tickets: context.tickets,
    tasks: context.tasks,
    brainFacts: context.brainFacts,
  });
  // Additive wire field: the worker prepends this fragment to the system
  // prompt it composes. Empty (neutral fallback) fragments are omitted.
  if (injection && injection.fragment) body.brandFragment = injection.fragment;
  const reply = normalizeReply(await post<unknown>('/interpret', body));
  return injection
    ? { ...reply, brandProvenance: injection.provenanceMeta, brandBadge: injection.badge }
    : reply;
}

/**
 * Ask the worker to run a deployed agent once and return its run narration.
 * `snapshot` is whatever store data the agent's job needs; the worker does
 * the real work (claims tickets, researches, etc.) and reports back.
 */
export async function runAgent(
  agent: { id: string; name: string; template: string; persona: string },
  snapshot: unknown,
  brand?: BrandPromptOptions,
): Promise<{ text: string } & BrandStampedFields> {
  const injection = resolveBrandInjection(brand);
  const body: Record<string, unknown> = { agent, snapshot, model: getSelectedModel() };
  if (injection && injection.fragment) body.brandFragment = injection.fragment;
  const out = await post<unknown>('/run', body);
  if (
    typeof out !== 'object' ||
    out === null ||
    typeof (out as { text?: unknown }).text !== 'string'
  ) {
    throw new AgentWorkerError('Agent-worker /run returned an unexpected shape.');
  }
  const result = { text: (out as { text: string }).text };
  return injection
    ? { ...result, brandProvenance: injection.provenanceMeta, brandBadge: injection.badge }
    : result;
}

/** Validate + coerce an untrusted worker reply into a safe `AgentReply`. */
function normalizeReply(reply: unknown): AgentReply {
  if (typeof reply !== 'object' || reply === null) {
    throw new AgentWorkerError('Agent-worker /interpret returned an unexpected shape.');
  }
  const r = reply as { text?: unknown; actions?: unknown };
  const text = typeof r.text === 'string' ? r.text : '';
  const actions = Array.isArray(r.actions)
    ? r.actions
        .filter(
          (a): a is AgentReply['actions'][number] =>
            typeof a === 'object' &&
            a !== null &&
            typeof (a as { kind?: unknown }).kind === 'string',
        )
        // Strip wire-only rationale/evidence extras and stamp worker
        // update_brain proposals 'inferred' — see proposalWhy.ts.
        .map(sanitizeWorkerAction)
    : [];
  if (!text && actions.length === 0) {
    throw new AgentWorkerError('Agent-worker /interpret returned no content.');
  }
  return { text, actions };
}
